import { useCallback, useEffect, useMemo, useState } from "react";
import { getClientPortalDashboard } from "../api/clientPortalApi";
import {
  fallbackPublishingQueue,
  fallbackRecentActivity,
  fallbackTopInterest,
  shouldUseDevFallback,
} from "../data/clientPortalDashboardFallback";

const ACTIVE_VIEW_STORAGE_KEY = "midpoint.clientPortal.activeViewId";

export function getStoredActiveClientViewId() {
  try {
    return localStorage.getItem(ACTIVE_VIEW_STORAGE_KEY) || null;
  } catch {
    return null;
  }
}

export function setStoredActiveClientViewId(clientViewId) {
  try {
    if (clientViewId) {
      localStorage.setItem(ACTIVE_VIEW_STORAGE_KEY, clientViewId);
    } else {
      localStorage.removeItem(ACTIVE_VIEW_STORAGE_KEY);
    }
  } catch {
    /* ignore storage errors */
  }
}

const EMPTY_DASHBOARD = {
  clientUser: { email: "", firstName: "", lastName: "", role: "" },
  clientOrg: { clientOrgId: "", clientName: "", status: "" },
  assignedClientViews: [],
  activeClientView: null,
  metrics: {
    newOpportunities: 0,
    needsFollowUp: 0,
    totalCommunities: 0,
    activeCommunities: 0,
    draftCommunities: 0,
    totalCourses: 0,
    activeCourses: 0,
    pendingCourses: 0,
    opportunityTrend: null,
  },
  siteStatus: {
    siteStatusLabel: "Not configured",
    publishingQueueCount: 0,
    lastPublishedAt: null,
  },
  recentOpportunities: [],
  publishingQueue: [],
  pipeline: [],
  topInterest: [],
  recentActivity: [],
  stubbedSections: [],
};

function normalizeMetrics(raw = {}) {
  return {
    newOpportunities: raw.newOpportunityCount ?? raw.newOpportunities ?? 0,
    needsFollowUp: raw.needsFollowUpCount ?? raw.needsFollowUp ?? 0,
    totalCommunities: raw.totalCommunityCount ?? raw.totalCommunities ?? 0,
    activeCommunities: raw.activeCommunityCount ?? raw.activeCommunities ?? 0,
    draftCommunities: raw.draftCommunityCount ?? raw.draftCommunities ?? 0,
    totalCourses: raw.totalCourseCount ?? raw.totalCourses ?? 0,
    activeCourses: raw.activeCourseCount ?? raw.activeCourses ?? 0,
    pendingCourses: raw.pendingCourseCount ?? raw.pendingCourses ?? 0,
    opportunityTrend: raw.opportunityTrend ?? null,
  };
}

function normalizeDashboard(apiDashboard) {
  if (!apiDashboard) {
    return EMPTY_DASHBOARD;
  }

  const stubbedSections = apiDashboard.stubbedSections || [];
  let publishingQueue = apiDashboard.publishingQueue || [];
  let topInterest = apiDashboard.topInterest || [];
  let recentActivity = apiDashboard.recentActivity || [];

  if (
    publishingQueue.length === 0 &&
    shouldUseDevFallback(stubbedSections, "publishingQueue")
  ) {
    publishingQueue = fallbackPublishingQueue;
  }
  if (topInterest.length === 0 && shouldUseDevFallback(stubbedSections, "topInterest")) {
    topInterest = fallbackTopInterest;
  }
  if (
    recentActivity.length === 0 &&
    shouldUseDevFallback(stubbedSections, "recentActivity")
  ) {
    recentActivity = fallbackRecentActivity;
  }

  return {
    clientUser: {
      email: apiDashboard.clientUser?.email || "",
      firstName: apiDashboard.clientUser?.firstName || "",
      lastName: apiDashboard.clientUser?.lastName || "",
      role: apiDashboard.clientUser?.role || "",
    },
    clientOrg: {
      id: apiDashboard.clientOrg?.clientOrgId || "",
      name: apiDashboard.clientOrg?.clientName || "",
      status: apiDashboard.clientOrg?.status || "",
    },
    assignedClientViews: (apiDashboard.assignedClientViews || []).map((view) => ({
      id: view.clientViewId,
      name: view.name,
      slug: view.slug,
      status: view.status,
      publicUrl: view.publicUrl,
    })),
    activeClientView: apiDashboard.activeClientView
      ? {
          id: apiDashboard.activeClientView.clientViewId,
          name: apiDashboard.activeClientView.name,
          slug: apiDashboard.activeClientView.slug,
          status: apiDashboard.activeClientView.status,
          publicUrl: apiDashboard.activeClientView.publicUrl,
          lastPublishedAt: apiDashboard.activeClientView.lastPublishedAt,
        }
      : null,
    metrics: normalizeMetrics(apiDashboard.metrics),
    siteStatus: {
      siteStatusLabel: apiDashboard.siteStatus?.siteStatusLabel || "Not configured",
      publishingQueueCount: apiDashboard.siteStatus?.publishingQueueCount ?? 0,
      lastPublishedAt: apiDashboard.siteStatus?.lastPublishedAt ?? null,
    },
    recentOpportunities: apiDashboard.recentOpportunities || [],
    publishingQueue,
    pipeline: apiDashboard.pipeline || [],
    topInterest,
    recentActivity,
    stubbedSections,
  };
}

export function useClientPortalDashboard(initialViewId) {
  const [selectedViewId, setSelectedViewIdState] = useState(initialViewId || null);
  const [rawDashboard, setRawDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const setSelectedViewId = useCallback((viewId) => {
    setSelectedViewIdState(viewId);
    setStoredActiveClientViewId(viewId);
  }, []);

  const loadDashboard = useCallback(async (viewId) => {
    setLoading(true);
    try {
      const body = await getClientPortalDashboard(viewId ? { clientViewId: viewId } : {});
      setRawDashboard(body.dashboard);
      setErrorMessage("");
    } catch (error) {
      setErrorMessage(error.message || "Failed to load dashboard.");
      setRawDashboard(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboard(selectedViewId);
  }, [loadDashboard, selectedViewId]);

  useEffect(() => {
    const handleViewChanged = (event) => {
      if (event.detail?.clientViewId) {
        setSelectedViewId(event.detail.clientViewId);
      }
    };
    window.addEventListener("client-portal-view-changed", handleViewChanged);
    return () => window.removeEventListener("client-portal-view-changed", handleViewChanged);
  }, [setSelectedViewId]);

  const dashboard = useMemo(() => normalizeDashboard(rawDashboard), [rawDashboard]);

  return {
    dashboard,
    loading,
    errorMessage,
    selectedViewId: selectedViewId || dashboard.activeClientView?.id || null,
    setSelectedViewId,
    reload: () => loadDashboard(selectedViewId),
  };
}

export default useClientPortalDashboard;
